import React from "react";
import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAdAccount } from '@/contexts/AdAccountContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Combobox } from '@/components/ui/combobox';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2, AlertCircle } from 'lucide-react';
import { ISO_COUNTRIES } from '@/constants/countries';
import { supabase } from '@/integrations/supabase/client';

type ObjectiveType = 'sales' | 'awareness' | 'traffic' | 'engagement' | 'leads';

const BID_STRATEGIES_BY_OBJECTIVE: Record<ObjectiveType, { value: string; label: string }[]> = {
  sales: [
    { value: 'highest_volume', label: 'Highest Volume' },
    { value: 'cost_cap', label: 'Cost Per Result Goal' },
    { value: 'bid_cap', label: 'Bid Cap' },
    { value: 'roas', label: 'ROAS Goal' },
  ],
  traffic: [
    { value: 'highest_volume', label: 'Highest Volume' },
    { value: 'cost_cap', label: 'Cost Per Result Goal' },
    { value: 'bid_cap', label: 'Bid Cap' },
  ],
  engagement: [
    { value: 'highest_volume', label: 'Highest Volume' },
    { value: 'cost_cap', label: 'Cost Per Result Goal' },
    { value: 'bid_cap', label: 'Bid Cap' },
  ],
  leads: [
    { value: 'highest_volume', label: 'Highest Volume' },
    { value: 'cost_cap', label: 'Cost Per Result Goal' },
    { value: 'bid_cap', label: 'Bid Cap' },
  ],
  awareness: [
    { value: 'highest_volume', label: 'Highest Volume' },
    { value: 'bid_cap', label: 'Bid Cap' },
  ],
};

const CreateCampaign = () => {
  const { user, loading: authLoading } = useAuth();
  const { selectedAdAccountId, selectedAdAccountName } = useAdAccount();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [campaignName, setCampaignName] = useState('');
  const [status, setStatus] = useState('PAUSED');
  const [objective, setObjective] = useState<ObjectiveType>('sales');
  const [advantageCatalogAds, setAdvantageCatalogAds] = useState(false);
  const [catalogId, setCatalogId] = useState('');
  const [budgetStrategy, setBudgetStrategy] = useState('ad_set');
  const [budgetType, setBudgetType] = useState('daily_budget');
  const [budgetAmount, setBudgetAmount] = useState('');
  const [shareBudget, setShareBudget] = useState(false);
  const [bidStrategy, setBidStrategy] = useState('highest_volume');
  const [spendCap, setSpendCap] = useState('');
  const [specialAdCategories, setSpecialAdCategories] = useState('none');
  const [specialAdCategoriesCountry, setSpecialAdCategoriesCountry] = useState('');

  const availableBidStrategies = useMemo(() => BID_STRATEGIES_BY_OBJECTIVE[objective], [objective]);

  // Reset bid strategy when objective changes and current selection is invalid
  useEffect(() => {
    const valid = availableBidStrategies.some((s) => s.value === bidStrategy);
    if (!valid) {
      setBidStrategy('highest_volume');
    }
  }, [objective, availableBidStrategies, bidStrategy]);

  // Reset catalog fields when objective changes away from sales
  useEffect(() => {
    if (objective !== 'sales') {
      setAdvantageCatalogAds(false);
      setCatalogId('');
    }
  }, [objective]);

  const handleObjectiveChange = (val: string) => {
    setObjective(val as ObjectiveType);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!campaignName.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Campaign name is required',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedAdAccountId) {
      toast({
        title: 'Validation Error',
        description: 'Please select an ad account from the sidebar',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);

    try {
      const payload: Record<string, string | number | null | undefined> = {
        ad_account_id: selectedAdAccountId,
        campaign_name: campaignName,
        objective,
        status,
        budget_strategy: budgetStrategy,
        special_ad_categories: specialAdCategories,
      };

      // Advantage+ Catalog Ads — only for sales
      if (objective === 'sales') {
        payload.advantage_catalog_ads = advantageCatalogAds ? 'on' : 'off';
        if (advantageCatalogAds && catalogId) {
          payload.catalog_id = catalogId;
        } else {
          payload.catalog_id = null;
        }
      } else {
        payload.advantage_catalog_ads = 'off';
        payload.catalog_id = null;
      }

      if (budgetStrategy === 'campaign') {
        payload.budget_type = budgetType;
        payload.budget_amount = parseFloat(budgetAmount) || 0;
        payload.bid_strategy = bidStrategy;
      } else {
        payload.share_budget = shareBudget ? 'on' : 'off';
        if (shareBudget) {
          payload.bid_strategy = bidStrategy;
        }
        const parsedSpendCap = parseFloat(spendCap);
        if (parsedSpendCap > 0) {
          payload.spend_cap = parsedSpendCap;
        }
      }

      if (specialAdCategories !== 'none' && specialAdCategoriesCountry) {
        payload.special_ad_categories_country = specialAdCategoriesCountry;
      }

      const { data, error: proxyError } = await supabase.functions.invoke(
        'n8n-proxy',
        {
          body: {
            action: 'CREATE_CAMPAIGN',
            payload,
          },
        }
      );

      if (proxyError) {
        throw new Error(proxyError.message || 'Failed to create campaign');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      toast({
        title: 'Success',
        description: 'Campaign created!',
      });

      navigate('/launcher/campaigns');
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create campaign',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    navigate('/auth');
    return null;
  }

  return (
    <div className="p-6">
      <header className="mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/launcher/campaigns')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">Create Campaign</h1>
        </div>
      </header>

      <div className="max-w-2xl">
        {!selectedAdAccountId ? (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-amber-600">
                <AlertCircle className="h-5 w-5" />
                <p>Please select an Ad Account from the sidebar to continue.</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>New Campaign</CardTitle>
              <CardDescription>
                Creating campaign for: <strong>{selectedAdAccountName}</strong>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">

              {/* Campaign Name */}
              <div className="space-y-2">
                <Label htmlFor="campaign-name">Campaign Name *</Label>
                <Input
                  id="campaign-name"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="Enter campaign name"
                  required
                />
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PAUSED">Paused</SelectItem>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Objective */}
              <div className="space-y-2">
                <Label htmlFor="objective">Objective</Label>
                <Select value={objective} onValueChange={handleObjectiveChange}>
                  <SelectTrigger id="objective">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="awareness">Awareness</SelectItem>
                    <SelectItem value="traffic">Traffic</SelectItem>
                    <SelectItem value="engagement">Engagement</SelectItem>
                    <SelectItem value="leads">Leads</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Advantage+ Catalog Ads — only for sales */}
              {objective === 'sales' && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Advantage+ Catalog Ads</Label>
                      <p className="text-sm text-muted-foreground">Enable catalog-based advertising</p>
                    </div>
                    <Switch checked={advantageCatalogAds} onCheckedChange={setAdvantageCatalogAds} />
                  </div>

                  {advantageCatalogAds && (
                    <div className="space-y-2">
                      <Label htmlFor="catalog-id">Catalog ID</Label>
                      <Input
                        id="catalog-id"
                        value={catalogId}
                        onChange={(e) => setCatalogId(e.target.value)}
                        placeholder="Enter catalog ID"
                      />
                    </div>
                  )}
                </>
              )}

              {/* Budget Strategy */}
              <div className="space-y-2">
                <Label htmlFor="budget-strategy">Budget Strategy</Label>
                <Select value={budgetStrategy} onValueChange={setBudgetStrategy}>
                  <SelectTrigger id="budget-strategy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ad_set">Ad Set Budget</SelectItem>
                    <SelectItem value="campaign">Campaign Budget</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Campaign Budget Options */}
              {budgetStrategy === 'campaign' && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="budget-type">Budget Type</Label>
                    <Select value={budgetType} onValueChange={setBudgetType}>
                      <SelectTrigger id="budget-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily_budget">Daily Budget</SelectItem>
                        <SelectItem value="lifetime_budget">Lifetime Budget</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="budget-amount">Budget Amount</Label>
                    <Input
                      id="budget-amount"
                      type="number"
                      value={budgetAmount}
                      onChange={(e) => setBudgetAmount(e.target.value)}
                      placeholder="Enter budget amount"
                      min="0"
                      step="0.01"
                    />
                  </div>

                </>
              )}

              {/* Ad Set Budget Options */}
              {budgetStrategy === 'ad_set' && (
                <>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Share Budget</Label>
                      <p className="text-sm text-muted-foreground">Share budget across ad sets</p>
                    </div>
                    <Switch checked={shareBudget} onCheckedChange={setShareBudget} />
                  </div>

                  {/* Bid Strategy — visible when Share Budget is ON */}
                  {shareBudget && (
                    <div className="space-y-2">
                      <Label htmlFor="bid-strategy-adset">Bid Strategy</Label>
                      <Select value={bidStrategy} onValueChange={setBidStrategy}>
                        <SelectTrigger id="bid-strategy-adset">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {availableBidStrategies.map((s) => (
                            <SelectItem key={s.value} value={s.value}>
                              {s.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="spend-cap">Campaign Spending Limit</Label>
                    <Input
                      id="spend-cap"
                      type="number"
                      value={spendCap}
                      onChange={(e) => setSpendCap(e.target.value)}
                      placeholder="No limit"
                      min="0"
                      step="0.01"
                    />
                    <p className="text-sm text-muted-foreground">
                      Optional. The maximum total amount this campaign can spend.
                    </p>
                  </div>
                </>
              )}

              {/* Special Ad Categories */}
              <div className="space-y-2">
                <Label htmlFor="special-ad-categories">Special Ad Categories</Label>
                <Select value={specialAdCategories} onValueChange={setSpecialAdCategories}>
                  <SelectTrigger id="special-ad-categories">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="employment">Employment</SelectItem>
                    <SelectItem value="housing">Housing</SelectItem>
                    <SelectItem value="social">Social</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Special Ad Category Country */}
              {specialAdCategories !== 'none' && (
                <div className="space-y-2">
                  <Label>Special Ad Category Country</Label>
                  <Combobox
                    options={ISO_COUNTRIES.map((c) => ({
                      value: c.code,
                      label: `${c.name} (${c.code})`,
                    }))}
                    value={specialAdCategoriesCountry}
                    onValueChange={setSpecialAdCategoriesCountry}
                    placeholder="Select country..."
                    searchPlaceholder="Search by name or code..."
                    emptyMessage="No country found."
                  />
                  <p className="text-sm text-muted-foreground">
                    Select the country where this campaign will run.
                  </p>
                </div>
              )}

                {/* Submit Button */}
                <div className="flex gap-4 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/launcher/campaigns')}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} className="flex-1">
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create Campaign'
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CreateCampaign;
